// components/MemoryTags.jsx — Anamoria SPA
// v1.2 — W2 polish (Sept 10, 2026): dropped the "TAGS" label and the empty-state helper
//   sentence from the editable row — the "+ Add a tag" affordance is self-describing.
// v1.1 — W2 UI refinement (Sept 10, 2026): AddTagToAll is now a quiet text trigger that
//   reveals its input on tap (progressive disclosure), replacing the dashed box. Copy
//   unchanged. MemoryTags chip/edit behaviour untouched.
// v1.0 — W2 member tags (Sept 8, 2026): per-memory free-text tag chips.
//   Read-only mode (feed display) renders chips only. Edit mode adds an "Add a tag"
//   affordance (chip input) + per-chip remove, cap 5, <=50 chars, client-side
//   autocomplete from `suggestions`. Gated by the caller on member_tags_enabled.
//   API is the caller's: onAdd(label) / onRemove(tagId) → PATCH /memories/{id}
//   { addTag } / { removeTagId } (anamoria-memories v2.33). Copy: brand-voice.
import { useState, useRef, useMemo } from 'react';
import styles from './MemoryTags.module.css';

export const MAX_TAGS = 5;
export const MAX_TAG_LEN = 50;

const norm = (s) => (s || '').trim().toLowerCase();

export default function MemoryTags({
  tags = [],
  onAdd,
  onRemove,
  suggestions = [],
  readOnly = false,
}) {
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);
  const atMax = tags.length >= MAX_TAGS;
  const existing = useMemo(() => new Set(tags.map((t) => norm(t.label))), [tags]);
  const matches = useMemo(() => {
    const v = norm(value);
    if (!v) return [];
    return suggestions.filter((s) => norm(s).includes(v) && !existing.has(norm(s))).slice(0, 5);
  }, [value, suggestions, existing]);

  // Read-only (feed display): chips only, nothing when empty.
  if (readOnly) {
    if (!tags.length) return null;
    return (
      <div className={styles.row}>
        {tags.map((t) => <span key={t.id} className={styles.chip}>{t.label}</span>)}
      </div>
    );
  }

  const commit = (raw) => {
    const clean = (raw ?? value).trim().slice(0, MAX_TAG_LEN);
    if (!clean || atMax) { setValue(''); return; }
    if (existing.has(norm(clean))) { setValue(''); return; } // already on this memory — no-op
    onAdd(clean);
    setValue('');
    inputRef.current?.focus();
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        {tags.map((t) => (
          <span key={t.id} className={styles.chip}>
            {t.label}
            <button
              type="button"
              className={styles.x}
              aria-label={`Remove tag ${t.label}`}
              onClick={() => onRemove(t.id)}
            >×</button>
          </span>
        ))}

        {!atMax && (adding ? (
          <span className={styles.inputWrap}>
            <input
              ref={inputRef}
              className={styles.input}
              value={value}
              maxLength={MAX_TAG_LEN}
              placeholder="A word or two"
              aria-label="Add a tag"
              autoFocus
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                else if (e.key === 'Escape') { setValue(''); setAdding(false); }
              }}
              onBlur={() => { if (value.trim()) commit(); setAdding(false); }}
            />
            {matches.length > 0 && (
              <ul className={styles.suggest} role="listbox">
                {matches.map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      className={styles.suggestBtn}
                      onMouseDown={(e) => { e.preventDefault(); commit(s); }}
                    >{s}</button>
                  </li>
                ))}
              </ul>
            )}
          </span>
        ) : (
          <button type="button" className={styles.addBtn} onClick={() => setAdding(true)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            Add a tag
          </button>
        ))}

        {tags.length > 0 && <span className={styles.count}>{tags.length} of {MAX_TAGS}</span>}
      </div>
    </div>
  );
}

// Optional moment-level shortcut: adds one tag to every piece in the moment. A convenience,
// never binding — each piece stays fully editable below. A quiet trigger at rest; the input and
// its one-line explanation appear only on tap (progressive disclosure). Calls onAdd(label) once.
export function AddTagToAll({ onAdd }) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const commit = () => {
    const c = value.trim().slice(0, MAX_TAG_LEN);
    if (c) onAdd(c);
    setValue('');
    setOpen(false);
  };

  if (!open) {
    return (
      <div className={styles.allLine}>
        <button type="button" className={styles.allTrigger} onClick={() => setOpen(true)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          Add a tag to every piece
        </button>
      </div>
    );
  }

  return (
    <div className={styles.allOpen}>
      <input
        className={styles.input}
        value={value}
        maxLength={MAX_TAG_LEN}
        placeholder="A word or two"
        aria-label="Add a tag to every piece"
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setValue(''); setOpen(false); }
        }}
      />
      <button type="button" className={styles.allApply} onClick={commit}>Add to all</button>
      <p className={styles.allHint}>A shortcut. You can change or remove it on any piece below.</p>
    </div>
  );
}
